import { useCallback, useMemo, useState } from 'react';
import { DEFAULT_PER_PAGE } from '@/lib/config';
import type { QueryParams } from '@/lib/api/client';

/**
 * List view state: pagination + filters, debounced free-text. Kept local for simplicity;
 * routes may seed initial values from the URL. Resetting a filter returns to page 1.
 */
export interface ListState {
  page: number;
  perPage: number;
  filters: Record<string, string | boolean | undefined>;
  search: string;
  params: QueryParams;
  setPage: (p: number) => void;
  setPerPage: (n: number) => void;
  setFilter: (key: string, value: string | boolean | undefined) => void;
  setSearch: (q: string) => void;
  reset: () => void;
}

export function useListState(initialFilters: Record<string, string | boolean | undefined> = {}): ListState {
  const [page, setPageRaw] = useState(1);
  const [perPage, setPerPageRaw] = useState(DEFAULT_PER_PAGE);
  const [filters, setFilters] = useState(initialFilters);
  const [search, setSearchRaw] = useState('');

  const setPage = useCallback((p: number) => setPageRaw(Math.max(1, p)), []);
  const setPerPage = useCallback((n: number) => {
    setPerPageRaw(n);
    setPageRaw(1);
  }, []);
  const setFilter = useCallback((key: string, value: string | boolean | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPageRaw(1);
  }, []);
  const setSearch = useCallback((q: string) => {
    setSearchRaw(q);
    setPageRaw(1);
  }, []);
  const reset = useCallback(() => {
    setFilters(initialFilters);
    setSearchRaw('');
    setPageRaw(1);
  }, [initialFilters]);

  const params = useMemo<QueryParams>(() => {
    const p: QueryParams = { page, per_page: perPage };
    for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined && v !== '' && v !== false) p[k] = v as string | boolean;
    }
    if (search.trim()) p.q = search.trim();
    return p;
  }, [page, perPage, filters, search]);

  return { page, perPage, filters, search, params, setPage, setPerPage, setFilter, setSearch, reset };
}
